// 短少分配 — who gets the pieces when fewer arrive than were ordered.
//
// Magnus 2026-09-18, asked directly, chose ORDER TIME and nothing else: the earliest
// `ordered_at` is served first, whether that line is a customer's or the 門市 rail's. No
// per-arrival judgement at the warehouse, no standing priority for either side. That is a
// rule 出貨人員 can apply at 7am without deciding anything, which is the whole point.
//
// Pure on purpose. Every input it needs is already on the receiving page — `allOrders`
// holds every unshipped 蝦皮 / Shopify / 官網 order plus, now, every open 門市 parcel — so
// the allocation costs no query against a database that has been timing out, and it
// covers all four sources instead of the one or two a server-side query could afford.
//
// It decides ONLY the split. Nothing here writes: 出貨人員 still tick what they physically
// hold, and the numbers this returns are what the card shows them.

/** One claim on an arriving variant. `key` is opaque; the caller maps it back to a row. */
export interface DemandLine {
  key: string;
  source: 'customer' | 'counter';
  /** When the order was placed. ISO string or epoch ms — compared, never displayed. */
  orderedAt: string | number;
  /** Pieces still wanted. Lines of 0 or less are dropped. */
  qty: number;
  /** Shown beside the line on the card, e.g. an order number or 門市 #77. */
  label?: string;
}

export interface Allocation extends DemandLine {
  /** Pieces this line gets out of the arrival. */
  allocated: number;
  /** qty − allocated. >0 means this line was short-shipped. */
  shortfall: number;
  /** 1-based position in the queue, so the card can say 「排第 2」. */
  rank: number;
}

const timeOf = (v: string | number): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
  const t = Date.parse(v);
  // A line with an unreadable timestamp sorts LAST rather than first. Getting served
  // early is the valuable outcome, so an unparseable date must never win it by accident.
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

/**
 * Serve `available` pieces to `demands`, earliest order first.
 *
 * Ties (same instant, e.g. several lines of one parcel) fall back to `key` so the result
 * is deterministic — an allocation that reshuffles between two renders is one 出貨人員
 * cannot trust.
 *
 * Fills each line completely before moving on, rather than spreading the shortage: half a
 * size run is not a sale, and the counter cannot hang two of a three-piece facing either.
 */
export function allocateByOrderTime(demands: DemandLine[], available: number): Allocation[] {
  const queue = demands
    .filter((d) => d && Number.isFinite(d.qty) && d.qty > 0)
    .slice()
    .sort((a, b) => {
      const ta = timeOf(a.orderedAt);
      const tb = timeOf(b.orderedAt);
      if (ta !== tb) return ta - tb;
      return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
    });

  let left = Math.max(0, Math.floor(Number(available) || 0));
  return queue.map((d, i) => {
    const allocated = Math.min(d.qty, left);
    left -= allocated;
    return { ...d, allocated, shortfall: d.qty - allocated, rank: i + 1 };
  });
}

/** The allocation for one line, or null when that line is not in the queue. */
export function allocationFor(
  demands: DemandLine[], available: number, key: string,
): Allocation | null {
  return allocateByOrderTime(demands, available).find((a) => a.key === key) || null;
}

/** Total pieces the arrival cannot cover. 0 = everybody is whole. */
export const totalShortfall = (allocations: Allocation[]): number =>
  allocations.reduce((n, a) => n + a.shortfall, 0);
